import { FormEvent, useEffect } from "react";
import { FormikErrors } from "formik";
import { Button, Image, Input } from "@atoms";
import { AuthSection } from "@organisms";
import { localEnv, isProductionBetaVersion } from "@config";
import { useTranslation } from "react-i18next";

const colors = require("@theme/colors") as Record<string, any>;

export interface FormValues {
  emailAddress: string;
  password: string;
}

export type SignInPageTemplateProps = {
  values: FormValues;
  errors: FormikErrors<FormValues>;
  loading: boolean;
  handleInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleLoginWithGoogle: () => void;
  handleSignUpRedirect: () => void;
  handleForgotPasswordRedirect: () => void;
  handleSubmit: (e?: FormEvent<HTMLFormElement> | undefined) => void;
};

export const SignInPageTemplate: React.FC<SignInPageTemplateProps> = ({
  values,
  errors,
  loading,
  handleInputChange,
  handleLoginWithGoogle,
  handleSignUpRedirect,
  handleForgotPasswordRedirect,
  handleSubmit,
}: SignInPageTemplateProps): JSX.Element => {
  const { t, i18n } = useTranslation();
  const { emailAddress, password } = values;
  const { emailAddress: emailAddressError, password: passwordError } = errors;
  const languageToggleLabel =
    i18n.language === "fr" ? t("common.english") : t("common.french");

  const toggleLanguage = () => {
    const nextLang = i18n.language === "fr" ? "en" : "fr";
    i18n.changeLanguage(nextLang);
    if (typeof window !== "undefined") {
      localStorage.setItem("i18nextLng", nextLang);
    }
  };

  function onKeyDown(keyEvent: any) {
    if ((keyEvent.charCode || keyEvent.keyCode) === 13) {
      keyEvent.preventDefault();
      handleSubmit()
    }
  }

  return (
    <div className="min-h-screen flex">
      <AuthSection />

      {/* Right side - Login form */}
      <div className="w-full lg:w-1/2 min-h-screen flex items-center justify-center bg-white">
        <div className="w-full max-w-md px-8">
          {/* Title and subtitle */}
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              {t("auth.signin.title")}
            </h1>
            <p className="text-gray-600 text-sm">
              {t("auth.signin.subtitlePrompt")}{" "}
              <button
                onClick={handleSignUpRedirect}
                className="text-primary-medium hover:text-primary-dark font-medium text-sm"
              >
                {t("auth.signin.signUpLink")}
              </button>
            </p>
          </div>

          {/* Sign in form */}
          <form onSubmit={handleSubmit} onKeyDown={onKeyDown} className="space-y-6">
            <div>
              <Input
                name="emailAddress"
                variant="text"
                onChange={handleInputChange}
                placeholder={t("auth.signin.email")}
                value={emailAddress}
                errorMessage={emailAddressError}
              />
            </div>
            <div>
              <Input
                name="password"
                variant="password"
                onChange={handleInputChange}
                placeholder={t("auth.signin.password")}
                value={password}
                errorMessage={passwordError}
              />
            </div>

            {/* Forgot password & language toggle */}
            <div className="flex justify-between items-center">
              <Button
                variant="link"
                label={languageToggleLabel}
                onClick={toggleLanguage}
                className="text-sm font-normal text-primary-medium hover:text-primary-dark"
              />
              <Button
                variant="link"
                label={t("auth.common.forgotPassword")}
                onClick={handleForgotPasswordRedirect}
                className="text-sm font-normal text-primary-medium hover:text-primary-dark"
              />
            </div>

            {/* Sign in button */}
            <Button
              type="submit"
              variant="primary"
              label={t("auth.signin.submit")}
              stretch
              color="primary-medium"
              hoverColor="primary-dark"
              textColor="white"
              loading={loading}
            />

            {/* Separator */}
            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-white text-gray-500">{t("auth.common.or")}</span>
              </div>
            </div>

            {/* Social sign-in buttons */}
            <div className="space-y-4">
              <Button
                variant="secondary"
                onClick={handleLoginWithGoogle}
                label={t("auth.common.continueWithGoogle")}
                stretch
                withIcon="google"
                color="#FFFFFF"
                hoverColor="#ECEBE7"
                textColor="#201A15"
                borderColor={colors.neutral.medium}
              />
              {/* <Button
                variant="secondary"
                onClick={() => {}} // TODO: Add Apple sign-in handler
                label="Continue with Apple"
                stretch
                color="neutral-darkest"
              />
              <Button
                variant="secondary"
                onClick={() => {}} // TODO: Add SSO sign-in handler
                label="Continue with Single Sign-On (SSO)"
                stretch
                color="neutral-darkest"
              /> */}
            </div>
          </form>

          {/* Environment indicator */}
          {localEnv === "dev" ? (
            <div className="mt-4 text-center px-4 py-2 bg-yellow-100 text-yellow-800 rounded">
              {t("auth.common.devBanner")}
            </div>
          ) : isProductionBetaVersion && (
            <div className="mt-4 text-center px-4 py-2 bg-blue-100 text-blue-800 rounded">
              {t("auth.common.betaBanner")}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
